import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Panel } from '../components/ui/Panel';
import { SectionHeader } from '../components/ui/SectionHeader';
import { RangePicker, type Range } from '../components/ui/RangePicker';
import { KpiCard } from '../components/ui/KpiCard';
import { TrendChart } from '../components/charts/TrendChart';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdLightning } from '../components/SubIdLightning';
import { useAuth } from '../store/auth';
import { usePublisherLookup, useCampaignLookup } from '../hooks/useLookups';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { SubDetailDrawer } from '../components/subdetail/SubDetailDrawer';
import { RealtimePulseCard } from '../components/RealtimePulseCard';
import { formatNumber, formatCurrency, formatPercent, formatRelativeTime } from '../lib/utils';
import { Megaphone, Bell, CheckCheck } from 'lucide-react';
import type { ByCountryRow, ByPublisherRow } from './Overviews.types';

function utcDateInput(ts = Date.now()): string {
  return new Date(ts).toISOString().slice(0, 10);
}

// Shape gabungan /stats/hourly + /stats/timeseries untuk TrendChart.
// Field timeseries aktual: date/clicks/conversions/revenue (BUKAN
// conversionsApproved/revenueGross seperti dugaan awal brief).
interface TrendPoint {
  hour?: number;
  date?: string;
  conversionsApproved?: number;
  revenueGross?: number;
  clicks?: number;
  conversions?: number;
  revenue?: number;
}

export function OverviewsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const publisherLookup = usePublisherLookup();
  const campaignLookup = useCampaignLookup();
  const pollMult = usePollMultiplier();
  const qc = useQueryClient();

  const [range, setRange] = useState<Range>('today');
  const [customFrom, setCustomFrom] = useState(() => utcDateInput());
  const [customTo, setCustomTo] = useState(() => utcDateInput());
  const [drilldownTarget, setDrilldownTarget] = useState<
    { publisherId: number | null; subIdHint?: string } | undefined
  >(undefined);
  const customValid = customFrom <= customTo;
  const statsParams =
    range === 'custom' ? { range, from: customFrom, to: customTo } : { range };
  const rangeKey =
    range === 'custom' ? `${range}:${customFrom}:${customTo}` : range;

  const overview = useQuery({
    queryKey: ['stats-overview', rangeKey],
    queryFn: () =>
      apiClient.get<{
        clicksTotal: number;
        clicksUnique: number;
        clicksBot: number;
        clicksQuality: number;
        conversionsApproved: number;
        conversionsPending: number;
        revenueGross: number;
        payoutGross: number;
        profit: number;
        cr: number;
        epc: number;
        adSpend?: number;
        roiPct?: number | null;
        ecpa?: number | null;
        range: string;
      }>(`/api/v1/stats/overview`, statsParams),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  const byPublisher = useQuery({
    queryKey: ['stats-by-publisher', rangeKey],
    queryFn: () =>
      apiClient.get<ByPublisherRow[]>(
        `/api/v1/stats/by-publisher`,
        statsParams,
      ),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  const byCountry = useQuery({
    queryKey: ['stats-by-country', rangeKey],
    queryFn: () =>
      apiClient.get<ByCountryRow[]>(`/api/v1/stats/by-country`, statsParams),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // Top Campaigns panel (Stitch layar 1) — reuse endpoint Statistics
  interface ByCampaignLite {
    campaignId: number | null;
    clicksTotal: number;
    conversionsApproved: number;
    revenueGross: number;
    adSpend?: number;
    roiPct?: number | null;
  }
  const byCampaign = useQuery({
    queryKey: ['stats-by-campaign', rangeKey],
    queryFn: () => apiClient.get<ByCampaignLite[]>('/api/v1/stats/by-campaign', statsParams),
    refetchInterval: range === 'today' ? 15_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // Conversion Alerts panel (Stitch layar 1) — endpoint automation admin-only.
  const alertsQ = useQuery({
    queryKey: ['overview-alerts'],
    queryFn: () =>
      apiClient.get<
        Array<{
          id: number;
          severity: 'info' | 'warning' | 'critical';
          message: string;
          isRead: boolean;
          createdAt: number;
        }>
      >('/api/v1/alerts/alerts', { limit: 8 }),
    enabled: isAdmin,
    refetchInterval: 30_000 * pollMult,
    retry: false,
  });

  const ackAlert = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/v1/alerts/alerts/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overview-alerts'] }),
  });

  const ackAllAlerts = useMutation({
    mutationFn: () => apiClient.post('/api/v1/alerts/alerts/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overview-alerts'] }),
  });

  function severityDot(s: string): string {
    if (s === 'critical') return 'bg-neon-red';
    if (s === 'warning') return 'bg-neon-yellow';
    return 'bg-text-tertiary';
  }

  const data = overview.data ?? {
    clicksTotal: 0,
    clicksUnique: 0,
    clicksBot: 0,
    clicksQuality: 0,
    conversionsApproved: 0,
    conversionsPending: 0,
    revenueGross: 0,
    payoutGross: 0,
    profit: 0,
    cr: 0,
    epc: 0,
    adSpend: 0,
    roiPct: null,
    ecpa: null,
  };

  // Get hourly data for performance chart and heatmap
  const hourly = useQuery({
    queryKey: ['stats-hourly', JSON.stringify(statsParams)],
    queryFn: () =>
      apiClient.get<
        Array<{
          hour: number;
          conversionsApproved: number;
          revenueGross: number;
          payoutGross: number;
        }>
      >('/api/v1/stats/hourly', statsParams),
    refetchInterval: range === 'today' ? 60_000 * pollMult : false,
    enabled: range === 'today' || range === 'yesterday' || (range === 'custom' && customValid && customFrom === customTo),
  });

  // Trend chart (Recharts) — hourly mode untuk today/yesterday, daily series
  // untuk 7d/30d/custom. Terpisah dari query `hourly` di atas yang tetap
  // dipakai HourlyHeatmapChart + DailyHeatmapStrip.
  const hourlyMode = range === 'today' || range === 'yesterday';
  const trendQuery = useQuery({
    queryKey: ['stats-trend', JSON.stringify(statsParams), hourlyMode ? 'hourly' : 'series'],
    queryFn: (): Promise<TrendPoint[]> =>
      hourlyMode
        ? apiClient.get<TrendPoint[]>('/api/v1/stats/hourly', statsParams)
        : apiClient.get<TrendPoint[]>('/api/v1/stats/timeseries', statsParams),
    refetchInterval: range === 'today' ? 60_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header + Range Picker */}
      <div>
        <SectionHeader
          title={isAdmin ? 'OVERVIEW' : 'MY DASHBOARD'}
          subtitle={
            isAdmin
              ? 'ALL PUBLISHERS'
              : user?.publisherId
                ? `PUBLISHER: ${publisherLookup.getName(user.publisherId)}`
                : 'PUBLISHER: UNLINKED'
          }
          actions={
            <>
              <RangePicker value={range} onChange={setRange} />
              {range === 'custom' && (
                <div className="flex flex-wrap items-center gap-2 text-xs font-jetbrains-mono">
                  <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="input-cyber h-9 w-36" />
                  <span className="text-text-tertiary">TO</span>
                  <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="input-cyber h-9 w-36" />
                  {!customValid && <span className="text-neon-red">FROM MUST BE BEFORE TO</span>}
                </div>
              )}
            </>
          }
        />
        {range === 'today' && (
          <p className="text-[10px] font-jetbrains-mono text-yellow-500/80 mt-1">
            [I] TODAY CLICKS HAVE ~30-60S AE INGESTION LAG. FOR SUB-2S LIVE STREAM, SEE CLICKS PAGE.
          </p>
        )}
      </div>

      {/* ROW 1: SIX KPI CELLS */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        <KpiCard label="TOTAL CLICKS" value={formatNumber(data.clicksTotal)} />
        <KpiCard label="CONVERSIONS" value={formatNumber(data.conversionsApproved)} accent="text-neon-magenta" />
        <KpiCard label="CR" value={formatPercent(data.cr, 2)} accent="text-neon-green" />
        <KpiCard label="REVENUE" value={formatCurrency(data.revenueGross)} accent="text-neon-yellow" />
        {isAdmin && <KpiCard label="AD COST" value={formatCurrency(data.adSpend ?? 0)} accent="text-neon-orange" />}
        {isAdmin && (
          <KpiCard
            label="ROI"
            value={data.roiPct == null ? '—' : formatPercent(data.roiPct, 1)}
            accent={(data.roiPct ?? 0) >= 0 ? 'text-neon-green' : 'text-neon-red'}
          />
        )}
      </div>

      {/* ROW 2: RECHARTS TREND (hourly 24H untuk today/yesterday, daily series sisanya).
          Normalisasi kedua shape ke {label, clicks, conversions, revenue} —
          /timeseries memang mengembalikan date/clicks/conversions/revenue
          (lihat apps/api/src/routes/stats.ts /timeseries), bukan conversionsApproved. */}
      <Panel level={1} bracket={true}>
        <div className="px-4 py-3 border-b border-neon-cyan/20 flex items-center justify-between">
          <h3 className="text-xs font-display text-neon-cyan/90 uppercase tracking-wide">
            CLICKS VS CONVERSIONS {hourlyMode ? '· 24H' : '· TREND'}
          </h3>
          <span className="text-[9px] font-jetbrains-mono text-text-tertiary">
            {hourlyMode ? 'HOURLY (UTC)' : 'DAILY (UTC)'}
          </span>
        </div>
        <div className="p-3 sm:p-4">
          {trendQuery.isLoading ? (
            <p className="text-center text-xs font-jetbrains-mono text-text-tertiary py-10">LOADING TREND...</p>
          ) : (
            <TrendChart
              showClicks={!hourlyMode}
              data={(trendQuery.data ?? []).map((d) =>
                'hour' in d
                  ? {
                      label: `${String(d.hour).padStart(2, '0')}:00`,
                      clicks: 0,
                      conversions: d.conversionsApproved ?? 0,
                      revenue: d.revenueGross ?? 0,
                    }
                  : {
                      // "2026-08-28" → "08/28" — sumbu X tetap padat di range panjang.
                      label: (d.date ?? '').slice(5).replace('-', '/'),
                      clicks: d.clicks ?? 0,
                      conversions: d.conversions ?? 0,
                      revenue: d.revenue ?? 0,
                    },
              )}
            />
          )}
        </div>
      </Panel>

      {/* ROW 2: GRID 7/5 - PERFORMANCE CHART + REALTIME PULSE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* LEFT: PerformanceChart (7 cols) */}
        <div className="lg:col-span-7">
          <Panel level={1} bracket={true}>
            <div className="px-4 py-3 border-b border-cyan-500/15">
              <h3 className="text-xs font-orbitron text-cyan-400/90 uppercase tracking-wide">PERFORMANCE · 24H</h3>
            </div>
            <div className="p-3 sm:p-4">
              {hourly.isLoading ? (
                <div className="text-center py-8">
                  <p className="text-xs font-jetbrains-mono text-text-tertiary">LOADING HOURLY DATA...</p>
                </div>
              ) : (
                <HourlyHeatmapChart
                  hourlyData={hourly.data ?? []}
                  rangeParams={statsParams}
                />
              )}
            </div>
          </Panel>
        </div>

        {/* RIGHT: RealtimePulseCard (5 cols) */}
        <div className="lg:col-span-5">
          <Panel level={1} bracket={true}>
            <div className="px-4 py-3 border-b border-cyan-500/15 flex items-center justify-between">
              <h3 className="text-xs font-orbitron text-cyan-400/90 uppercase tracking-wide">REALTIME PULSE</h3>
              <span className="flex items-center gap-1.5 text-[9px] font-jetbrains-mono uppercase text-text-tertiary">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                LIVE · &lt;2S
              </span>
            </div>
            <div className="p-3 sm:p-4">
              {range === 'today' ? (
                <RealtimePulseRestyled maxRows={6} />
              ) : (
                <div className="text-center py-8">
                  <p className="text-xs font-jetbrains-mono text-text-tertiary">REALTIME ONLY AVAILABLE FOR TODAY RANGE</p>
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>

      {/* ROW 3: HEATMAP STRIP 24 CELLS */}
      <Panel level={1} bracket={false}>
        <div className="px-4 py-3 border-b border-cyan-500/15 flex items-center justify-between">
          <h3 className="text-xs font-orbitron text-magenta-400/90 uppercase tracking-wide">HOURLY BREAKDOWN (UTC)</h3>
          <span className="text-[9px] font-jetbrains-mono text-text-tertiary">CONVERSIONS + REVENUE PER HOUR</span>
        </div>
        <div className="p-3 sm:p-4">
          {hourly.isLoading ? (
            <div className="text-center py-8">
              <p className="text-xs font-jetbrains-mono text-text-tertiary">LOADING HOURLY DATA...</p>
            </div>
          ) : (
            <DailyHeatmapStrip hourlyData={hourly.data ?? []} rangeParams={statsParams} />
          )}
        </div>
      </Panel>

      {/* ROW 4: FOUR PANELS (TOP PUBLISHERS, TOP CAMPAIGNS, TOP COUNTRIES, SYSTEM STATUS) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        {/* TOP PUBLISHERS */}
        <Panel level={1} bracket={true}>
          <div className="px-4 py-3 border-b border-cyan-500/15">
            <h3 className="text-xs font-orbitron text-cyan-400/90 uppercase tracking-wide flex items-center gap-1.5">
              TOP PUBLISHERS
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table-cyber w-full">
              <thead>
                <tr>
                  <th className="text-left text-[10px] font-orbitron uppercase">PUBLISHER</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">CLICKS</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">CONV</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">REVENUE</th>
                </tr>
              </thead>
              <tbody>
                {(byPublisher.data ?? []).slice(0, 6).map((row, i) => (
                  <tr
                    key={i}
                    onClick={() =>
                      setDrilldownTarget({
                        publisherId: row.publisherId ?? null,
                        subIdHint: row.subIdHint,
                      })
                    }
                    className="cursor-pointer hover:bg-neon-magenta/5"
                    title="CLICK FOR DETAILED BREAKDOWN"
                  >
                    <td className="font-jetbrains-mono text-xs text-cyan-400/90" title={row.publisherId ? `ID: ${row.publisherId}` : row.subIdHint ? `UNREGISTERED SUB_ID: ${row.subIdHint}` : undefined}>
                      {row.publisherId != null ? (
                        <SubIdLightning subId={publisherLookup.getSubId(row.publisherId)}>
                          {publisherLookup.getName(row.publisherId)}
                        </SubIdLightning>
                      ) : row.subIdHint ? (
                        <span className="text-text-primary">{row.subIdHint}</span>
                      ) : (
                        <span className="text-text-tertiary italic">unlinked</span>
                      )}
                    </td>
                    <td className="text-right numeric font-jetbrains-mono text-xs">{formatNumber(row.clicksQuality)}</td>
                    <td className="text-right numeric font-jetbrains-mono text-xs text-neon-magenta">{formatNumber(row.conversionsApproved)}</td>
                    <td className="text-right numeric font-jetbrains-mono text-xs text-yellow-400">{formatCurrency(row.revenueGross)}</td>
                  </tr>
                ))}
                {(byPublisher.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-text-tertiary py-8 font-jetbrains-mono text-xs">NO DATA YET — WAITING FOR TRAFFIC...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* TOP CAMPAIGNS (Stitch layar 1) */}
        <Panel level={1} bracket={true}>
          <div className="px-4 py-3 border-b border-cyan-500/15">
            <h3 className="text-xs font-orbitron text-cyan-400/90 uppercase tracking-wide flex items-center gap-1.5">
              <Megaphone size={12} />
              TOP CAMPAIGNS
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table-cyber w-full">
              <thead>
                <tr>
                  <th className="text-left text-[10px] font-orbitron uppercase">CAMPAIGN</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">CLICKS</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">CONV</th>
                  {isAdmin && (
                    <th className="text-right text-[10px] font-orbitron uppercase">ROI</th>
                  )}
                  <th className="text-right text-[10px] font-orbitron uppercase">REVENUE</th>
                </tr>
              </thead>
              <tbody>
                {(byCampaign.data ?? [])
                  .slice()
                  .sort((a, b) => b.revenueGross - a.revenueGross)
                  .slice(0, 6)
                  .map((row, i) => {
                    const name = row.campaignId != null ? campaignLookup.nameFromId(row.campaignId) : '—';
                    return (
                      <tr key={i}>
                        <td className="font-jetbrains-mono text-xs text-cyan-400/90 truncate max-w-[120px]" title={name}>
                          {name}
                        </td>
                        <td className="text-right numeric font-jetbrains-mono text-xs">{formatNumber(row.clicksTotal)}</td>
                        <td className="text-right numeric font-jetbrains-mono text-xs text-neon-magenta">{formatNumber(row.conversionsApproved)}</td>
                        {isAdmin && (
                          <td
                            className={`text-right numeric font-jetbrains-mono text-xs ${
                              row.roiPct == null
                                ? 'text-text-tertiary'
                                : row.roiPct >= 0
                                  ? 'text-green-400'
                                  : 'text-red-400'
                            }`}
                          >
                            {row.roiPct == null ? '—' : formatPercent(row.roiPct, 0)}
                          </td>
                        )}
                        <td className="text-right numeric font-jetbrains-mono text-xs text-yellow-400">{formatCurrency(row.revenueGross)}</td>
                      </tr>
                    );
                  })}
                {(byCampaign.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} className="text-center text-text-tertiary py-8 font-jetbrains-mono text-xs">NO DATA YET</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* TOP COUNTRIES */}
        <Panel level={1} bracket={true}>
          <div className="px-4 py-3 border-b border-cyan-500/15">
            <h3 className="text-xs font-orbitron text-cyan-400/90 uppercase tracking-wide flex items-center gap-1.5">
              TOP COUNTRIES
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table-cyber w-full">
              <thead>
                <tr>
                  <th className="text-left text-[10px] font-orbitron uppercase">COUNTRY</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">CLICKS</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">CONV</th>
                  <th className="text-right text-[10px] font-orbitron uppercase">REVENUE</th>
                </tr>
              </thead>
              <tbody>
                {(byCountry.data ?? []).slice(0, 6).map((row, i) => (
                  <tr key={i}>
                    <td>
                      <CountryFlag iso2={row.country} showCode size={16} />
                    </td>
                    <td className="text-right numeric font-jetbrains-mono text-xs">{formatNumber(row.clicksTotal)}</td>
                    <td className="text-right numeric font-jetbrains-mono text-xs text-neon-magenta">{formatNumber(row.conversionsApproved)}</td>
                    <td className="text-right numeric font-jetbrains-mono text-xs text-yellow-400">{formatCurrency(row.revenueGross)}</td>
                  </tr>
                ))}
                {(byCountry.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-text-tertiary py-8 font-jetbrains-mono text-xs">NO DATA YET</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* SYSTEM STATUS — hanya baris dengan data nyata; row mati (queue
            depth/backup) dihapus karena selalu "—" dan membingungkan. */}
        <SystemStatusPanel pollMult={pollMult} isAdmin={isAdmin} />
      </div>

      {/* ROW 5: CONVERSION ALERTS (Stitch layar 1 — kartu alert dengan
          severity dot + Acknowledge; data dari evaluator automation P0-B) */}
      {isAdmin && (
        <Panel level={1} bracket={false}>
          <div className="px-4 py-3 border-b border-cyan-500/15 flex items-center justify-between">
            <h3 className="text-xs font-orbitron text-magenta-400/90 uppercase tracking-wide flex items-center gap-1.5">
              <Bell size={12} />
              CONVERSION ALERTS
              <span className="text-[9px] font-jetbrains-mono text-text-tertiary normal-case tracking-normal">
                automation evaluator · last 8
              </span>
            </h3>
            {(alertsQ.data ?? []).some((a) => !a.isRead) && (
              <button
                onClick={() => ackAllAlerts.mutate()}
                disabled={ackAllAlerts.isPending}
                className="inline-flex items-center gap-1 text-[10px] font-jetbrains-mono uppercase text-neon-cyan hover:text-white transition-colors disabled:opacity-40"
              >
                <CheckCheck size={12} />
                ACK ALL
              </button>
            )}
          </div>
          <div className="divide-y divide-border-subtle/60">
            {(alertsQ.data ?? []).map((a) => (
              <div
                key={a.id}
                className={`px-4 py-2.5 flex items-center gap-2 ${!a.isRead ? 'border-l-2 border-l-cyan-400' : ''}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${severityDot(a.severity)}`} />
                <p className={`text-xs font-jetbrains-mono min-w-0 flex-1 truncate ${a.isRead ? 'text-text-tertiary' : 'text-text-primary'}`} title={a.message}>
                  {a.message}
                </p>
                <span className="text-[9px] font-jetbrains-mono text-text-tertiary shrink-0">
                  {formatRelativeTime(a.createdAt)}
                </span>
                {!a.isRead && (
                  <button
                    onClick={() => ackAlert.mutate(a.id)}
                    disabled={ackAlert.isPending}
                    className="text-[9px] font-jetbrains-mono uppercase px-1.5 py-0.5 border border-cyan-400/40 text-cyan-400 rounded-sm hover:bg-cyan-400/10 transition-colors disabled:opacity-40 shrink-0"
                  >
                    ACK
                  </button>
                )}
              </div>
            ))}
            {(alertsQ.data ?? []).length === 0 && !alertsQ.isLoading && (
              <div className="px-4 py-6 text-center font-jetbrains-mono text-[11px] text-text-tertiary">
                no alerts — evaluator belum menemukan anomali
              </div>
            )}
            {alertsQ.isLoading && (
              <div className="px-4 py-6 text-center font-jetbrains-mono text-[11px] text-text-tertiary">
                loading alerts...
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* FOOTER: FRESHNESS LEGEND */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[10px] font-jetbrains-mono text-text-tertiary">
        <span className="uppercase tracking-wider">FRESHNESS:</span>
        <span>CONVERSIONS <span className="text-green-400">10-20S</span></span>
        <span>CLICKS <span className="text-yellow-400">45-90S</span></span>
        <span>REALTIME <span className="text-cyan-400">&lt;2S</span></span>
        <span>OVERVIEW CACHE <span className="text-neon-magenta">60-90S</span></span>
      </div>

      <SubDetailDrawer
        publisherId={drilldownTarget?.publisherId}
        subIdHint={drilldownTarget?.subIdHint}
        onClose={() => setDrilldownTarget(undefined)}
        range={range}
        customFrom={customFrom}
        customTo={customTo}
      />
    </div>
  );
}

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

/**
 * Restyled RealtimePulseCard using Panel structure internally.
 * Existing RealtimePulseCard uses Card from ./ui/Card which now delegates to Panel.
 */
function RealtimePulseRestyled({ maxRows = 6 }: { maxRows?: number }) {
  return <RealtimePulseCard maxRows={maxRows} />;
}

/**
 * PerformanceChart wrapper that uses hourly data for today/yesterday.
 * Shows bar chart with toggle chips [KLIK|KONV|REV] if supported.
 */
function HourlyHeatmapChart({
  hourlyData,
  rangeParams,
}: {
  hourlyData: Array<{ hour: number; conversionsApproved: number; revenueGross: number }>;
  rangeParams: { range: string; from?: string; to?: string };
}) {
  const [metric, setMetric] = useState<'clicks' | 'conversions' | 'revenue'>('conversions');

  // Prepare rows: ensure all 24 hours present
  const rows = Array.from({ length: 24 }, (_, hour) => {
    const existing = hourlyData.find((r) => r.hour === hour);
    return {
      hour,
      clicks: 0, // NOT PROVIDED BY EXISTING API
      conversions: existing?.conversionsApproved ?? 0,
      revenue: existing?.revenueGross ?? 0,
    };
  }).sort((a, b) => a.hour - b.hour);

  const max =
    metric === 'clicks'
      ? Math.max(1, ...rows.map((r) => r.clicks))
      : metric === 'conversions'
      ? Math.max(1, ...rows.map((r) => r.conversions))
      : Math.max(0.0001, ...rows.map((r) => r.revenue));

  const heightPct = (v: number) => ((v / max) * 100).toFixed(2);

  return (
    <div>
      {/* Metric toggle pills */}
      <div className="flex items-center gap-2 mb-3">
        {(['clicks', 'conversions', 'revenue'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMetric(m)}
            className={`px-3 py-1.5 text-[10px] font-orbitron uppercase tracking-wider rounded border transition-all ${
              metric === m
                ? 'bg-neon-magenta/20 border-neon-magenta text-neon-magenta'
                : 'bg-bg-tertiary border-border-subtle text-text-secondary hover:border-cyan-400/40'
            }`}
          >
            {m === 'clicks' ? 'KLIK' : m === 'conversions' ? 'KONV' : 'REV'}
          </button>
        ))}
      </div>

      {/* Chart container */}
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded min-h-[160px] flex flex-col justify-end">
        {rows.length === 0 ? (
          <p className="text-[11px] font-jetbrains-mono text-text-tertiary italic text-center py-4">NO DATA IN THIS RANGE</p>
        ) : (
          <>
            {/* Bars */}
            <div className="flex items-end gap-[2px] h-32 px-1 border-b border-border-subtle/40 pb-px">
              {rows.map((r) => {
                const v = metric === 'clicks' ? r.clicks : metric === 'conversions' ? r.conversions : r.revenue;
                const h = heightPct(v);
                const color =
                  metric === 'clicks' ? 'bg-cyan-400' : metric === 'conversions' ? 'bg-neon-magenta' : 'bg-yellow-400';
                return (
                  <div
                    key={r.hour}
                    className="flex-1 h-full relative group"
                    title={`${String(r.hour).padStart(2, '0')}:00 · ${v.toFixed(0)} ${metric}`}
                  >
                    <div
                      className={`absolute bottom-0 left-0 right-0 rounded-t-sm transition-all group-hover:opacity-80 ${color}`}
                      style={{ height: `${h}%`, minHeight: v > 0 ? '2px' : '0' }}
                    />
                  </div>
                );
              })}
            </div>

            {/* X-axis labels tiap 3 jam */}
            <div className="flex justify-between text-[9px] font-jetbrains-mono text-text-tertiary mt-1 px-1">
              {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
                <span key={h}>{String(h).padStart(2, '0')}</span>
              ))}
              <span>24</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Daily heatmaps strip: 24 sel kotak sama besar, intensitas cyan opacity proporsional revenue.
 * Tooltip mono "HH:00 · N conv · $X". Label jam tiap 3h tetap.
 */
function DailyHeatmapStrip({
  hourlyData,
  rangeParams,
}: {
  hourlyData: Array<{ hour: number; conversionsApproved: number; revenueGross: number }>;
  rangeParams: { range: string; from?: string; to?: string };
}) {
  // Normalize to 24 hours
  const rows = Array.from({ length: 24 }, (_, hour) => {
    const existing = hourlyData.find((r) => r.hour === hour);
    return {
      hour,
      conversions: existing?.conversionsApproved ?? 0,
      revenue: existing?.revenueGross ?? 0,
    };
  }).sort((a, b) => a.hour - b.hour);

  // Use revenue for intensity; fallback to conv if revenue = 0
  const maxValue = Math.max(
    0.0001,
    ...rows.map((r) => (r.revenue > 0 ? r.revenue : r.conversions)),
  );

  return (
    <div className="flex gap-1 h-20">
      {rows.map((r) => {
        const intensityVal = r.revenue > 0 ? r.revenue : r.conversions;
        const opacity = Math.max(0.15, (intensityVal / maxValue) * 0.85);
        const tooltipLabel = `${String(r.hour).padStart(2, '0')}:00 · ${r.conversions} conv · ${formatCurrency(r.revenue)}`;

        return (
          <div
            key={r.hour}
            className="flex-1 relative group cursor-help"
            title={tooltipLabel}
          >
            {/* Heat cell */}
            <div
              className="w-full h-full bg-cyan-400 rounded-sm transition-all group-hover:opacity-100"
              style={{ opacity }}
            />
            {/* Tooltip (CSS-only) */}
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-bg-secondary border border-cyan-500/40 rounded px-2 py-1 text-[9px] font-jetbrains-mono whitespace-nowrap z-10 pointer-events-none text-text-primary">
              {tooltipLabel}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * SYSTEM STATUS panel - baris mono: tracker ✓, queue depth —, optimizer ON/OFF, last backup —
 */
type OptimizerStatus = { metric?: string; rate?: string; enabled: boolean };

async function fetchSmartlinkStatus(): Promise<OptimizerStatus> {
  try {
    // Fetch first campaign via GET /api/v1/campaigns/{id}/smartlink
    const campaigns = await apiClient.get<Array<{ id: number }>>('/api/v1/campaigns');
    if (!campaigns || campaigns.length === 0) {
      return { enabled: false };
    }

    const firstCampaignId = campaigns[0]?.id;
    if (firstCampaignId == null) {
      return { enabled: false };
    }

    const smartlink = await apiClient.get<{
      enabled: boolean;
      optimization_metric?: string;
      optimization_rate?: string;
    }>(`/api/v1/campaigns/${firstCampaignId}/smartlink`);

    if (!smartlink?.enabled) {
      return { enabled: false };
    }

    return {
      enabled: true,
      metric: smartlink.optimization_metric,
      rate: smartlink.optimization_rate,
    };
  } catch (err) {
    console.warn('[SYSTEM STATUS] Failed to fetch smartlink:', err);
    return { enabled: false };
  }
}

function SystemStatusPanel({ pollMult, isAdmin }: { pollMult: number; isAdmin: boolean }) {
  const [optimizerStatus, setOptimizerStatus] = useState<OptimizerStatus>({ enabled: false });

  // Poll optimizer status every 30s
  const optimizerQuery = useQuery({
    queryKey: ['system-status-optimizer'],
    queryFn: fetchSmartlinkStatus,
    refetchInterval: 30_000 * pollMult,
  });

  // Konversi pending = antrean yang menunggu keputusan network — sinyal
  // operasional yang benar-benar ada endpoint-nya (admin saja).
  const pendingQuery = useQuery({
    queryKey: ['system-status-pending'],
    queryFn: () =>
      apiClient.get<{ pending: number }>('/api/v1/stats/pending-count'),
    refetchInterval: 60_000 * pollMult,
    enabled: isAdmin,
    retry: false,
  });

  useEffect(() => {
    if (optimizerQuery.data) {
      setOptimizerStatus(optimizerQuery.data);
    }
  }, [optimizerQuery.data]);

  return (
    <Panel level={1} bracket={true}>
      <div className="px-4 py-3 border-b border-cyan-500/15">
        <h3 className="text-xs font-orbitron text-cyan-400/90 uppercase tracking-wide">SYSTEM STATUS</h3>
      </div>
      <div className="p-4 space-y-2">
        {/* TRACKER */}
        <div className="flex items-center justify-between gap-2 font-jetbrains-mono text-[11px]">
          <span className="text-text-secondary">TRACKER</span>
          <span className="text-green-400">✓ ONLINE</span>
        </div>

        {/* PENDING CONVERSIONS (admin) */}
        {isAdmin && (
          <div className="flex items-center justify-between gap-2 font-jetbrains-mono text-[11px]">
            <span className="text-text-secondary" title="Postback menunggu konfirmasi status dari network">
              PENDING CONVERSIONS
            </span>
            <span className={pendingQuery.data?.pending ? 'text-yellow-400' : 'text-text-tertiary'}>
              {pendingQuery.data?.pending ?? '—'}
            </span>
          </div>
        )}

        {/* OPTIMIZER */}
        <div className="flex items-center justify-between gap-2 font-jetbrains-mono text-[11px]">
          <span className="text-text-secondary">AUTO-OPTIMIZER</span>
          {optimizerStatus.enabled ? (
            <span className="text-yellow-400">
              ON · {optimizerStatus.metric ?? 'METRIC'}@{optimizerStatus.rate ?? 'RATE'}%
            </span>
          ) : (
            <span className="text-red-400">OFF</span>
          )}
        </div>
      </div>
    </Panel>
  );
}

// Re-export types for external use
export type { ByCountryRow, ByPublisherRow };
